import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { erApiFeil } from '../../api/feil.ts';
import { useAdminUsers, useAktiverAdminUser, type AdminUser } from '../../api/useAdminUsers.ts';
import { useMe } from '../../api/useMe.ts';
import { Toast, type ToastInnhold } from '../../innboks/Toast.tsx';
import { romIkon, romNavn, type Rom } from '../../skall/rom.ts';
import { useTilgang } from '../../skall/tilgang.ts';
import { useSmal } from '../../skall/useSmalSkjerm.ts';
import { Ikon } from '../../status/Ikon.tsx';
import { BareEier } from '../BareEier.tsx';
import { Kopier } from '../../innstillinger/Kopier.tsx';
import { TekniskeBryter } from '../../innstillinger/Rad.tsx';
import { erUte, RolleOrd, StatusOrd, TilgangLinje, Topp } from './felles.tsx';
import { harTilgang, summen } from './modell.ts';
import { Radiogruppe } from './Radiogruppe.tsx';

/** Det «Legg til person» sender med til lista, så kvitteringen står der personen havner. */
export interface ListeTilstand {
  kvittering?: string;
}

/**
 * Lista (`/organisasjon/brukere`, M4.8 steg 3). Én liste uansett rom; rommene er egenskaper ved
 * personen (Tilgang-kolonnen), aldri kolonner (DD-68 presisert 2026-09-25). Filteret finnes BARE
 * fra to rom (DD-02) og er klientside på `GET /v1/admin/users`. Ingen fot (DD-58 presisert):
 * ingenting her er et utkast — «Aktiver» er ett kall uten dialog (DD-68).
 *
 * Lista er aldri tom — eieren som ser den, står i den. En tomtilstand finnes derfor ikke, og
 * «Kunne ikke laste …» sier «kunne ikke», aldri «ingen» (`ux-flow-architect`).
 */
export function ListeSide() {
  const tilgang = useTilgang();
  if (!tilgang.kjent) return <Laster />;
  if (!tilgang.eier) return <BareEier />;
  return <Lista />;
}

function Laster() {
  return (
    <div role="status" aria-busy="true" aria-label="Laster panelbrukere" className="panel-skjelett">
      <span className="panel-skjelett-linje panel-skjelett-tittel" />
      <span className="panel-skjelett-linje" />
      <span className="panel-skjelett-linje" />
    </div>
  );
}

function Lista() {
  const me = useMe();
  const liste = useAdminUsers(true);
  const aktiver = useAktiverAdminUser();
  const smal = useSmal();
  const naviger = useNavigate();
  const sted = useLocation();
  const [filter, setFilter] = useState<Rom | 'alle'>('alle');
  // H3.5 (P4): panelbrukerens id står bak «Vis tekniske navn» — det er den hypotesehendelsene bærer i
  // recipients[]. Lesehjelp, ikke en innstilling: valget lagres ikke.
  const [tekniske, setTekniske] = useState(false);
  const [toast, setToast] = useState<ToastInnhold | null>(() => {
    const k = (sted.state as ListeTilstand | null)?.kvittering;
    return k ? { tekst: k } : null;
  });

  const rommene = me.data?.rooms ?? [];
  const flere = rommene.length > 1;
  const megId = me.data?.id;
  const alle = liste.data ?? [];
  const rader = filter === 'alle' ? alle : alle.filter((b) => harTilgang(b, filter));
  const forst = filter === 'alle' ? null : filter;

  const lukkToast = () => {
    setToast(null);
    // Kvitteringen er vist; en omlasting skal ikke vise den igjen.
    if (sted.state) void naviger(sted.pathname, { replace: true, state: null });
  };

  const aktiverPerson = (b: AdminUser) =>
    aktiver
      .mutateAsync(b.id)
      .then(() => setToast({ tekst: `${b.email} har tilgang igjen.` }))
      .catch((e: unknown) => setToast({ tekst: erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.', feil: true }));

  const leggTil = (
    <Link to="/organisasjon/brukere/ny" className="panel-knapp-sekundaer panel-pb-knapplenke">
      <Ikon navn="add-outline" />
      Legg til person
    </Link>
  );

  const s = summen(alle);
  const ingress = liste.isPending ? (
    <span className="panel-skjelett-linje panel-pb-skjelett-sum" aria-hidden="true" />
  ) : liste.isError ? null : (
    <span data-testid="pb-summen">
      <b>{s.personer}</b>
      {!smal ? ` · ${s.roller}` : null}
      {s.venter ? ` · ${s.venter} ${smal ? 'venter' : 'venter på første innlogging'}` : null}
      {s.ute ? ` · ${s.ute} deaktivert` : null}
    </span>
  );

  return (
    <>
      <div className="panel-pb">
        <Topp tilbake={{ til: 'Organisasjon', href: '/organisasjon' }} tittel="Panelbrukere" ingress={ingress} handling={leggTil} skjultTittel />
        <div className="panel-pb-innhold panel-pb-bred">
          {liste.isPending ? (
            <Laster />
          ) : liste.isError ? (
            // Dagens tekst. «Kunne ikke», aldri «ingen».
            <div role="alert" className="panel-pb-varsel">
              <Ikon navn="warning-outline" />
              <span>Kunne ikke laste panelbrukerne.</span>
              <button type="button" className="panel-knapp-sekundaer" onClick={() => void liste.refetch()}>
                Prøv igjen
              </button>
            </div>
          ) : (
            <>
              <div className="panel-pb-verktoy">
                <TekniskeBryter paa={tekniske} onChange={setTekniske} />
              </div>
              {flere ? (
                <div className="panel-pb-filter">
                  {/* Etiketten er kravet (avvik 4): uten den leser segmentene som skallets rom-velger. */}
                  <span className="panel-pb-filter-etikett" aria-hidden="true">
                    Vis hvem som har tilgang til
                  </span>
                  <Radiogruppe
                    navn="Vis hvem som har tilgang til"
                    valg={[{ verdi: 'alle' as const, ord: 'Alle', tall: alle.length }, ...rommene.map((r) => ({ verdi: r, ord: romNavn(r), ikon: romIkon(r), tall: alle.filter((b) => harTilgang(b, r)).length }))]}
                    aktiv={filter}
                    onVelg={setFilter}
                  />
                </div>
              ) : null}
              {smal ? <Rader rader={rader} rommene={rommene} forst={forst} megId={megId} tekniske={tekniske} /> : <Tabell rader={rader} rommene={rommene} forst={forst} megId={megId} tekniske={tekniske} onAktiver={aktiverPerson} />}
            </>
          )}
        </div>
      </div>
      {/* Ikke en fot i DD-58s forstand — ingen primærknapp, ingenting lagres. Omrisset i
          tommelsonen fordi «Legg til person» ellers ligger over folden på 400 px. */}
      {smal ? <div className="panel-pb-tommel">{leggTil}</div> : null}
      {toast ? <Toast innhold={toast} onLukk={lukkToast} /> : null}
    </>
  );
}

function Tabell({ rader, rommene, forst, megId, tekniske, onAktiver }: { rader: AdminUser[]; rommene: readonly Rom[]; forst: Rom | null; megId: string | undefined; tekniske: boolean; onAktiver: (b: AdminUser) => void }) {
  return (
    <table className="panel-pb-tabell" aria-label="Panelbrukere">
      <thead>
        <tr>
          <th scope="col">Adresse</th>
          <th scope="col">Rolle</th>
          {/* Overskriften sier rekkefølgen når filteret snur den (avvik 12, godkjent). */}
          <th scope="col">{forst && rommene.length > 1 ? `Tilgang · ${romNavn(forst)} først` : 'Tilgang'}</th>
          <th scope="col">
            <span className="panel-skjult">Handling</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rader.map((b) => {
          const deg = b.id === megId;
          const ute = erUte(b);
          return (
            <tr key={b.id} className={ute ? 'panel-pb-ute' : undefined} data-testid={`pb-rad-${b.id}`}>
              <td className="panel-pb-hvem">
                <span className="panel-pb-adresse">{b.email}</span>
                <StatusOrd bruker={b} deg={deg} />
                {tekniske ? <IdRad bruker={b} /> : null}
              </td>
              <td className="panel-pb-rollecelle">
                <RolleOrd bruker={b} />
              </td>
              <td>
                <TilgangLinje bruker={b} rommene={rommene} forst={forst} testid={`pb-tilgang-${b.id}`} />
              </td>
              <td className="panel-pb-handling">
                {deg ? (
                  <span className="panel-pb-deg">Deg</span>
                ) : ute ? (
                  <button type="button" className="panel-knapp-sekundaer panel-pb-liten" aria-label={`Aktiver ${b.email}`} onClick={() => onAktiver(b)}>
                    Aktiver
                  </button>
                ) : (
                  <Link to={`/organisasjon/brukere/${b.id}`} className="panel-knapp-lenke" aria-label={`Endre tilgang for ${b.email}`}>
                    Endre tilgang
                  </Link>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Under 900 px: raden er tre linjer — adressen med rollen til høyre, tilgangslinjen i body,
 * statusen som meta-linje — og HELE raden er lenken til personen (48 minimum). Den deaktiverte
 * raden åpner også personen: «Aktiver kontoen» står i fotens plass der.
 */
function Rader({ rader, rommene, forst, megId, tekniske }: { rader: AdminUser[]; rommene: readonly Rom[]; forst: Rom | null; megId: string | undefined; tekniske: boolean }) {
  return (
    <ul className="panel-pb-rader" aria-label="Panelbrukere">
      {rader.map((b) => {
        const deg = b.id === megId;
        return (
          <li key={b.id} data-testid={`pb-rad-${b.id}`}>
            <Link to={`/organisasjon/brukere/${b.id}`} className={erUte(b) ? 'panel-pb-radlenke panel-pb-ute' : 'panel-pb-radlenke'} aria-label={deg ? `${b.email} — deg` : b.email}>
              <span className="panel-pb-radtopp">
                <span className="panel-pb-adresse">{b.email}</span>
                <RolleOrd bruker={b} />
              </span>
              <TilgangLinje bruker={b} rommene={rommene} forst={forst} testid={`pb-tilgang-${b.id}`} />
              <StatusOrd bruker={b} deg={deg} />
            </Link>
            {/* Utenfor lenken: «Kopier» er en egen knapp, og en knapp inne i en lenke er to mål i ett. */}
            {tekniske ? <IdRad bruker={b} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Id-en under adressen, med «Kopier» (H3.5, P4): hypotesehendelsene sier hvem som skal varsles som
 * panelbrukerens id — aldri e-post — og her kobler leietakeren id-en til personen. En id er ingen
 * hemmelighet. Bare med «Vis tekniske navn»; ellers finnes den ikke i DOM (DD-50).
 */
function IdRad({ bruker }: { bruker: AdminUser }) {
  return (
    <span className="panel-pb-id">
      <code>{bruker.id}</code>
      <Kopier hva={`id-en til ${bruker.email}`} verdi={bruker.id} />
    </span>
  );
}
